"use client";

import Image from "next/image";
import {
  Star,
  FolderPlus,
  BarChart3,
  Zap,
  MessageSquare,
  TrendingUp,
  ShieldCheck,
  ClipboardCheck,
  Globe,
} from "lucide-react";
import certificationG2Badges from "@/assets/images/footer/certifications/g2-badges.png";
import CmsFeedbackForm from "../CmsFeedbackForm/CmsFeedbackForm";
import { FEEDBACK_SOURCES } from "@/utils/feedback-form";

const STATS = [
  { value: "40%", label: "Admin work automated" },
  { value: "10x", label: "Faster task completion" },
  { value: "50+", label: "AI Agents" },
];

const AI_FEATURES = [
  {
    icon: FolderPlus,
    title: "AI Project Creation",
    description: "Generate projects and tasks from a single prompt",
  },
  {
    icon: BarChart3,
    title: "Real-Time Intelligence",
    description: "Live insights and analytics across your workspace",
  },
  {
    icon: Zap,
    title: "Sprints & Execution",
    description: "Automate workflows and track progress effortlessly",
  },
  {
    icon: MessageSquare,
    title: "Ask AI for Anything",
    description: "Get answers, summaries, and suggestions on demand",
  },
];

const TRUST_BADGES = [
  {
    title: "SOC2 Certified",
    icon: <ShieldCheck className="h-4 w-4 text-[#6B7280]" />,
  },
  {
    title: "HIPAA Compliant",
    icon: <ClipboardCheck className="h-4 w-4 text-[#6B7280]" />,
  },
  {
    title: "G2 High Performer",
    icon: <Star className="h-4 w-4 text-[#6B7280]" />,
  },
  {
    title: "GDPR Ready",
    icon: <Globe className="h-4 w-4 text-[#6B7280]" />,
  },
];

const BookDemoLayout = () => {
  return (
    <section className="min-h-screen bg-gradient-to-b from-[#F5F3FF] to-white pb-20 pt-24 xl:pb-24 xl:pt-[120px]">
      <div className="container max-w-[1288px]">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-[#6049E7] text-[#6049E7]" />
              <span className="text-[14px] font-medium text-[#6049E7]">
                The World&apos;s First AI WorkOS
              </span>
            </div>
            <h1 className="text-[32px] font-bold leading-tight text-[#202020] xl:text-[44px]">
              One Platform. <br /> All Your{" "}
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                AI Needs.
              </span>
            </h1>
            <p className="text-[18px] font-bold text-[#202020]">
              Stop Searching Across 10 Tools. Get Answers in Seconds.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {STATS.map((stat, index) => (
                <div
                  key={index}
                  className="rounded-2xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
                >
                  <p className="text-[24px] font-bold bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent xl:text-[28px]">
                    {stat.value}
                  </p>
                  <p className="text-[14px] font-normal text-[#6B7280]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#781E8F] to-[#422CBB] p-6">
              <div className="mb-4 flex items-center gap-2">
                <Star className="h-4 w-4 text-white" />
                <span className="text-[16px] font-semibold tracking-wide text-white">
                  What you&apos;ll get in this demo:
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {AI_FEATURES.map((feature, index) => (
                  <div
                    key={index}
                    className="rounded-[4px] bg-[#6049E7] p-4 transition-colors hover:bg-[#553ED0]"
                  >
                    <feature.icon className="mb-2 h-6 w-6 text-white" />
                    <p className="font-semibold text-white">{feature.title}</p>
                    <p className="text-[14px] text-[#C4B5FD]">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-4">
              <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#6B7280]" />
                <p className="text-[14px] font-medium text-[#6B7280]">
                  Don&apos;t just take our word — see what G2 says
                </p>
              </div>
              <Image
                src={certificationG2Badges}
                alt="Kroolo G2 awards and certifications badges"
                className="h-auto w-full lg:max-w-[600px] object-contain"
                draggable={false}
              />
            </div>
          </div>
          <div className="lg:sticky lg:top-32">
            <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
              <div className="border-b border-[#E5E7EB] px-6 py-4">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#22C55E] animate-pulse" />
                  <span className="text-[14px] font-medium text-[#202020]">
                    Demo slots available this week
                  </span>
                </div>
              </div>
              <div className="space-y-6 p-6 xl:p-8">
                <div>
                  <h2 className="text-[24px] font-bold text-[#202020]">
                    Book Your Personalized Demo
                  </h2>
                  <p className="mt-1 text-[14px] text-[#6B7280]">
                    Get a 1-on-1 walkthrough with our AI solutions experts.
                  </p>
                </div>
                <CmsFeedbackForm
                  source={FEEDBACK_SOURCES.BOOK_DEMO}
                  submitLabel="Book Demo"
                  footerNote={
                    <>
                      <p className="text-center text-[14px] text-[#6B7280]">
                        No credit card required • Response within 1 business day
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-2 border-t border-[#E5E7EB] pt-4">
                        {TRUST_BADGES.map((badge, index) => (
                          <span
                            key={index}
                            className="flex items-center gap-1 text-[12px] font-medium text-[#6B7280]"
                          >
                            <span className="text-[#6B7280]">{badge.icon}</span>
                            <span>{badge.title}</span>
                          </span>
                        ))}
                      </div>
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookDemoLayout;
